/**
 * Utilities for managing Shopify integration parameters across authentication flow
 */

const SHOPIFY_PARAMS_KEY = 'shopify_integration_params'
const EXPIRY_HOURS = 24

export interface ShopifyParams {
  shop: string
  host?: string
  hmac?: string
  shopify: string
  timestamp: string
}

/**
 * Store Shopify params in localStorage
 */
export const storeShopifyParams = (shop: string, host?: string, hmac?: string): void => {
  const params: ShopifyParams = {
    shop,
    host,
    hmac,
    shopify: 'true',
    timestamp: new Date().toISOString()
  }
  localStorage.setItem(SHOPIFY_PARAMS_KEY, JSON.stringify(params))
}

/**
 * Get stored Shopify params from localStorage
 * Returns null if not found or expired
 */
export const getStoredShopifyParams = (): ShopifyParams | null => {
  const stored = localStorage.getItem(SHOPIFY_PARAMS_KEY)
  if (!stored) return null

  try {
    const parsed: ShopifyParams = JSON.parse(stored)

    // check if expired (older than 24 hours)
    const storedTime = new Date(parsed.timestamp).getTime()
    const now = new Date().getTime()
    const hoursSinceStored = (now - storedTime) / (1000 * 60 * 60)

    if (hoursSinceStored >= EXPIRY_HOURS) {
      // expired, clean up
      clearShopifyParams()
      return null
    }

    return parsed
  } catch (e) {
    console.error('Failed to parse stored Shopify params:', e)
    clearShopifyParams()
    return null
  }
}

const SHOPIFY_RESUME_KEY = 'shopify_resume_pending'

/**
 * Mark that the Shopify connect flow itself sent the user off to create an
 * organization (the mid-install "no orgs yet" hop). Only this marker authorizes
 * CreateOrganization to resume into /shopify-integration afterwards — the mere
 * EXISTENCE of stored params must not: they live for 24h and hijacked every
 * unrelated org creation into connecting a store the user never intended (and
 * each hijacked authorize invalidates the store's previous grant — see #1645).
 * sessionStorage on purpose: the mid-install hop is same-tab and immediate, so
 * the marker must not outlive the tab.
 */
export const markShopifyResumePending = (): void => {
  sessionStorage.setItem(SHOPIFY_RESUME_KEY, 'true')
}

/**
 * Consume (read + clear) the mid-install resume marker.
 */
export const consumeShopifyResumePending = (): boolean => {
  const pending = sessionStorage.getItem(SHOPIFY_RESUME_KEY) === 'true'
  sessionStorage.removeItem(SHOPIFY_RESUME_KEY)
  return pending
}

/**
 * Clear stored Shopify params from localStorage
 */
export const clearShopifyParams = (): void => {
  localStorage.removeItem(SHOPIFY_PARAMS_KEY)
  sessionStorage.removeItem(SHOPIFY_RESUME_KEY)
}

/**
 * Whether /shopify-integration should PERSIST the params on this load.
 *
 * Only on a FRESH arrival from Shopify — a signed install URL carries `shop` but not our internal
 * `shopify=true` flag. When we arrive via an INTERNAL resume redirect (CreateOrganization builds
 * the URL with `shopify=true` after a mid-install org creation), the params are already stored
 * and must NOT be re-stored: re-storing refreshes the 24h expiry, keeping a stale/abandoned flow
 * alive long after the merchant walked away (or cancelled on admin.shopify.com).
 */
export const shouldStoreShopifyParams = (search: string): boolean => {
  const p = new URLSearchParams(search)
  return !!p.get('shop') && p.get('shopify') !== 'true'
}

/**
 * Build query string from Shopify params
 */
export const buildShopifyQueryString = (params: ShopifyParams): string => {
  const queryParams = new URLSearchParams({
    shopify: params.shopify,
    shop: params.shop,
    hmac: params.hmac || '',
  })

  if (params.host) {
    queryParams.set('host', params.host)
  }

  return queryParams.toString()
}
